import {
  DrupalProductNode,
  DrupalProductVariationGroup,
  DrupalProductVariationType,
} from "@/types/hygiena-types";

/**
 * Add a variation to a variationGroups array
 *
 * @param {DrupalProductVariationGroup[]} variationGroups - An existing array of variation group objects.
 * @param {string} productNumber - A product number.
 * @param {string} [productSKU] - A product sku.
 * @param {string} [detail] - The product detail.
 * @param {string} [ausStoreLink] - An australian estore link.
 * @param {string} [usStoreLink] - A US store link.
 * @param {boolean} [lyokitModal] - Whether this is a lyokit modal.
 * @param {DrupalProductVariationType|null|undefined} [variationType] - The variation type.
 * @param {string|null|undefined} [variationValue] - The variation value.
 * @return void
 */
function addVariation(
  variationGroups: DrupalProductVariationGroup[],
  productNumber: string,
  productSKU?: string,
  detail?: string,
  ausStoreLink?: string,
  usStoreLink?: string,
  lyokitModal?: boolean,
  variationType?: DrupalProductVariationType|null|undefined,
  variationValue?: string|null|undefined,
): void {
  let groupMatch: boolean = false;
  for (const group of variationGroups) {
    // Either variation type/value matches, or the group and product has no variation setup; associate eitherway.
    const variationGroupMatch =  group?.variation?.type === variationType && group?.variation?.value === variationValue;
    const noVariationMatch = !group?.variation?.value && !group?.variation?.type && !variationType && !variationValue;
    if (variationGroupMatch || noVariationMatch) {
      group.productNumbers.push({
        number: productNumber,
        sku: productSKU,
        detail: detail,
        ausStoreLink,
        usStoreLink,
      });
      if (!group.modal && lyokitModal) {
        group.modal = true;
      }
      groupMatch = true;
      break;
    }
  }
  if (!groupMatch) {
    let variationGroup: DrupalProductVariationGroup = {
      productNumbers: [{
        number: productNumber,
        sku: productSKU,
        detail: detail,
        ausStoreLink,
        usStoreLink,
      }],
      modal: !!lyokitModal,
    }
    if (variationType && variationValue) {
      variationGroup.variation = {
        type: variationType,
        value: variationValue,
      }
    }
    variationGroups.push(variationGroup);
  }
}

/**
 * Extracts the product variations from the product node.
 *
 * @param {DrupalProductNode} node - A product node.
 * @return DrupalProductVariationGroup
 */
export function getProductVariations(node: DrupalProductNode): DrupalProductVariationGroup[] {
  // Build out a list of possible variation groups.
  const variationGroups: DrupalProductVariationGroup[] = [];
  if (Array.isArray(node?.field_product_vars)) {
    for (const variation of node.field_product_vars) {
      let variationType: DrupalProductVariationType|null = null;
      let variationValue: string|null = null;
      if (variation?.field_var_reactions?.name) {
        variationType = DrupalProductVariationType.Reactions;
        variationValue = variation.field_var_reactions.name;
      }
      else if (variation?.field_var_quantity?.name) {
        variationType = DrupalProductVariationType.Quantity;
        variationValue = variation.field_var_quantity.name;
      }
      else if (variation?.field_var_volume?.name) {
        variationType = DrupalProductVariationType.Volume;
        variationValue = variation.field_var_volume.name;
      }
      else if (variation?.field_var_tests?.name) {
        variationType = DrupalProductVariationType.Tests;
        variationValue = variation.field_var_tests.name;
      }
      else if (variation?.field_var_runs?.name) {
        variationType = DrupalProductVariationType.Runs;
        variationValue = variation.field_var_runs.name;
      }
      addVariation(
        variationGroups,
        variation.field_var_number,
        variation?.field_var_sku,
        variation?.field_var_detail,
        variation?.field_var_store_link_aus?.uri,
        variation?.field_var_store_link_us?.uri,
        variation?.field_var_lyokit_modal,
        variationType,
        variationValue,
      )
    }
  }
  return variationGroups.sort((a, b) => {
    if (typeof a?.variation?.value === 'string' && typeof b?.variation?.value === 'string') {
      if (!isNaN(parseInt(a.variation.value)) && !isNaN(parseInt(b.variation.value))) {
        return parseInt(a.variation.value) - parseInt(b.variation.value);
      }
      return b.variation.value?.localeCompare(a?.variation?.value);
    }
    if (typeof a?.variation?.value === 'number' && typeof b?.variation?.value === 'number') {
      return b.variation.value - a.variation.value;
    }
    return 0;
  });
}
